import { Theme, ActualTheme } from '@yandex-int/k-common/typings';
import b from 'b_';
import { Help, Link, MarkdownExtXContent, Text } from 'platform-components';
import { POPUP_THEMES, POPUP_DIRECTIONS, HELP_TYPE } from 'platform-components/constants';
import { SUBJECTS_SLUGS_TYPE } from 'platform-components/src/typings';
import { withRouterPath, InjectedProps as WithRouterPathHOC } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import * as userActions from 'common.components/user/actions';
import { getCurrentClass } from 'common.components/user/selectors';
import * as themesSelectors from 'store/themes/selectors';

import { ReduxTeacherStore, RootAction } from '../../../typings';

import './section-menu__column.scss';

export const helpProps = {
    popupProps: {
        theme: POPUP_THEMES.SCHOOL,
        direction: POPUP_DIRECTIONS.TOP,
        offsetTop: -12,
        hasArrow: true,
        mix: 'section-menu__column-help-popup',
    },
    type: HELP_TYPE.INFO,
};

interface StoreProps {
    isActualTheme: boolean;
    childThemes: Array<Theme>;
}

interface DispatchProps {
    setWizardVariables: typeof userActions.setWizardVariables;
}

interface HOCProps extends WithRouterPathHOC {}

interface OwnProps {
    theme: Theme;
    tooltipDescriptionId?: string;
}

interface Props extends DispatchProps, StoreProps, OwnProps, HOCProps {}

export class SectionMenuColumn extends React.Component<Props> {
    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    handleClickTheme = (themeId: number) => {
        const {
            match: {
                params: { subject },
            },
            setWizardVariables,
            isActualTheme,
        } = this.props;

        const params: {
            subject: SUBJECTS_SLUGS_TYPE;
            themeId: number;
            scenario?: 'promo' | 'school_program';
            rootThemeId?: number | null;
        } = {
            subject,
            themeId,
        };

        ym('reachGoal', 'teacher_lesson_creation_opened_subtopic', params);

        if (isActualTheme) {
            ym('reachGoal', 'teacher_lesson_creation_opened_actualtheme', params);
        }

        setWizardVariables({ themeClicked: true });
    };

    render() {
        const {
            getRouterPath,
            theme,
            match: {
                params: { themeId, classId },
            },
            location: { search },
            childThemes,
            tooltipDescriptionId,
            isActualTheme,
        } = this.props;
        const { staticUrls } = this.context;
        const selectedThemeId = themeId ? Number(themeId) : null;

        return (
            <div className="section-menu__column" key={theme.id}>
                <Text size={Text.SIZE.XL}>
                    <span className="section-menu__column-title">
                        <MarkdownExtXContent>{theme.name}</MarkdownExtXContent>
                        {tooltipDescriptionId && (
                            <Help {...helpProps} mix="section-menu__column-title-help">
                                <Text id={tooltipDescriptionId} type={Text.TYPE.MARKDOWN} />
                            </Help>
                        )}
                    </span>
                </Text>
                <div className="section-menu__column-content">
                    {childThemes.map((subTheme) => {
                        let path = classId ? staticUrls.labLibrarySubjectThemeStacks : staticUrls.deepLabStacks;

                        if (isActualTheme && (subTheme as ActualTheme).use_lessons) {
                            path = staticUrls.labLibrarySubjectThemeLessons;
                        }

                        return (
                            <Link
                                isRouter
                                key={subTheme.id}
                                mix={b('section-menu', 'column-item', { selected: subTheme.id === selectedThemeId })}
                                onClick={() => this.handleClickTheme(subTheme.id)}
                                theme={Link.THEME.STEALTHY}
                                to={`${getRouterPath(path, { themeId: subTheme.id })}${search}`}
                            >
                                <Text size={Text.SIZE.XL}>
                                    <MarkdownExtXContent>{subTheme.name}</MarkdownExtXContent>
                                </Text>
                            </Link>
                        );
                    })}
                </div>
            </div>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    return bindActionCreators(
        {
            setWizardVariables: userActions.setWizardVariables,
        },
        dispatch
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore, props: OwnProps & HOCProps): StoreProps => {
    const {
        location: { search },
        match: {
            params: { subject, classId },
        },
        theme: { id: themeId },
    } = props;
    const query = qs.parse(search);
    const currentTeam = getCurrentClass(storeState, { classId });
    const grade = currentTeam?.level || Number(query.grade) || null;
    const isActualTheme = themesSelectors.getIsActualTheme(storeState, { themeId });
    const courseId = getCurrentYearCourseId(storeState);
    const childThemes = isActualTheme
        ? themesSelectors.getActualChildThemes(storeState, { subject, courseId, themeId })
        : themesSelectors.getChildThemes(storeState, { subject, themeId, grade });

    return {
        childThemes,
        isActualTheme,
    };
};

export default withRouterPath(
    connect<StoreProps, DispatchProps, OwnProps & HOCProps>(mapStateToProps, mapDispatchToProps)(SectionMenuColumn)
);
